// @ts-nocheck
import React, { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Typography, Tabs, Tab, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  GeneralOverview,
  ProjectWiseOverview,
  UserDetailsPage,
  FeedbackDetailsPage,
  PIIDetailsPage,
} from '../../components/Dashboard';
import useGetProjectsInfo from '../../hooks/KnowledgeAI/useGetProjectsInfo';
import {
  useFetchFeedbackConversations,
  useFetchPIIConversations,
  FEEDBACK_TYPE_MAP,
} from '../../hooks/KnowledgeAI/useDashboardApi';

// ----------------------------------------------------------------------

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

// ----------------------------------------------------------------------

const DashboardPage = () => {
  const theme = useTheme();
  const { data: projectsData, loading: projectsLoading } = useGetProjectsInfo();

  // Navigation state — only routing/view concerns live here
  const [activeTab, setActiveTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [feedbackView, setFeedbackView] = useState<string | null>(null);
  const [piiView, setPiiView] = useState(false);
  // Tracks the project selected in ProjectWiseOverview so feedback API gets the right project_name
  const [selectedProjectName, setSelectedProjectName] = useState<string>('');

  // Feedback conversations is cross-cutting: it's triggered from any tab/view and navigates
  // to a top-level FeedbackDetailsPage, so it stays here.
  const {
    data: feedbackApiData,
    loading: feedbackApiLoading,
    fetchFeedbackConversations,
  } = useFetchFeedbackConversations();

  const { data: piiApiData, loading: piiApiLoading, fetchPIIConversations } = useFetchPIIConversations();

  const handleTabChange = useCallback((_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    setSelectedUser(null);
    setFeedbackView(null);
    setPiiView(false);
    if (newValue !== 1) setSelectedProjectName('');
  }, []);

  const handleUserClick = useCallback((username: string) => {
    setSelectedUser(username);
    setFeedbackView(null);
    setPiiView(false);
  }, []);

  const handleBackFromUser = useCallback(() => {
    setSelectedUser(null);
  }, []);

  // Receives feedbackType key (e.g. "fullWrong") and context is resolved here
  const handleFeedbackClick = useCallback(
    (feedbackType: string) => {
      setFeedbackView(feedbackType);
      const apiType = FEEDBACK_TYPE_MAP[feedbackType] || feedbackType;
      const projectName = activeTab === 1 ? selectedProjectName : '';
      const userName = selectedUser || '';
      fetchFeedbackConversations(apiType, projectName, userName);
    },
    [activeTab, selectedProjectName, selectedUser, fetchFeedbackConversations]
  );

  const handleBackFromFeedback = useCallback(() => {
    setFeedbackView(null);
  }, []);

  const handlePiiClick = useCallback(() => {
    setPiiView(true);
    const projectName = activeTab === 1 ? selectedProjectName : '';
    const userName = selectedUser || '';
    fetchPIIConversations(projectName, userName);
  }, [activeTab, selectedProjectName, selectedUser, fetchPIIConversations]);

  const handleBackFromPii = useCallback(() => {
    setPiiView(false);
  }, []);

  // Called by ProjectWiseOverview when the selected project changes (after normalization)
  const handleProjectNameChange = useCallback((normalizedName: string) => {
    setSelectedProjectName(normalizedName);
  }, []);

  const tabLabels = ['General', 'Project Wise'];
  const feedbackContextLabel = selectedUser ? `${selectedUser} Details` : tabLabels[activeTab];

  // Feedback data: empty while loading or when no API data yet
  const feedbackData = feedbackApiLoading ? [] : feedbackApiData?.length > 0 ? feedbackApiData : [];
  const piiData = piiApiLoading ? [] : piiApiData?.length > 0 ? piiApiData : [];

  return (
    <>
      <Helmet>
        <title>Dashboard | Knowledge AI</title>
      </Helmet>

      <Container
        id="dashboard"
        sx={{
          mb: 5,
          pl: { xs: 10 },
          '& .MuiCard-root': { bgcolor: theme.palette.mode === 'dark' ? '#302d3b' : '#f7f7f7' },
        }}
      >
        <Typography variant="h4" sx={{ mb: 3, mt: 4, color: theme.palette.mode === 'dark' ? '#C7C9C7' : '#333f48' }}>
          Dashboard
        </Typography>

        {feedbackView ? (
          <FeedbackDetailsPage
            feedbackType={feedbackView}
            conversations={feedbackData}
            onBack={handleBackFromFeedback}
            contextLabel={feedbackContextLabel}
            loading={feedbackApiLoading}
          />
        ) : piiView ? (
          <PIIDetailsPage
            conversations={piiData}
            onBack={handleBackFromPii}
            contextLabel={feedbackContextLabel}
            loading={piiApiLoading}
          />
        ) : !selectedUser ? (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs value={activeTab} onChange={handleTabChange}>
                <Tab label="General" />
                <Tab label="Project Wise" />
              </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
              <GeneralOverview
                onUserClick={handleUserClick}
                onFeedbackClick={handleFeedbackClick}
                onPiiClick={handlePiiClick}
                projects={projectsData}
                projectsLoading={projectsLoading}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <ProjectWiseOverview
                onUserClick={handleUserClick}
                onFeedbackClick={handleFeedbackClick}
                projects={projectsData}
                projectsLoading={projectsLoading}
                onProjectNameChange={handleProjectNameChange}
              />
            </TabPanel>
          </>
        ) : (
          <UserDetailsPage
            username={selectedUser}
            onBack={handleBackFromUser}
            contextLabel={tabLabels[activeTab]}
            onFeedbackClick={handleFeedbackClick}
          />
        )}
      </Container>
    </>
  );
};

export default DashboardPage;
